import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid
} from '@mui/material';
import {
  NavigateNext,
  NavigateBefore,
  Send,
  Timer,
  Warning
} from '@mui/icons-material';
import axios from 'axios';

const TestInterface = () => {
  const { category, testId } = useParams();
  const navigate = useNavigate();
  const [mcqs, setMcqs] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(30 * 60);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [testStarted, setTestStarted] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [testInstructions, setTestInstructions] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const hasSubmittedRef = useRef(false);
  const [joinStatus, setJoinStatus] = useState(null);

  useEffect(() => {
    fetchMCQs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, testId]);

  useEffect(() => {
    let timer;
    if (testStarted && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            if (!hasSubmittedRef.current) {
              handleSubmitTest(true); // Auto-submit when time runs out
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [testStarted, timeLeft]);

  const [testName, setTestName] = useState('');

  const fetchMCQs = async () => {
    try {
      let questions = [];
      if (testId) {
        // Fetch test details to show name and config
        try {
          const testRes = await axios.get(`/api/tests/${testId}`);
          const t = testRes.data.test;
          setTestName(t?.name || 'Test');
          if (t?.duration_minutes) {
            setDurationMinutes(t.duration_minutes);
            setTimeLeft(t.duration_minutes * 60);
          }
          setTestInstructions(t?.instructions || '');
          const latestJoin = t?.joinStatus || null;
          setJoinStatus(latestJoin);
          // Only fetch MCQs if approved per latest response
          if (latestJoin !== 'approved') {
            setMcqs([]);
            return;
          }
        } catch { }
        const response = await axios.get(`/api/tests/${testId}/mcqs`);
        questions = response.data.mcqs || [];
      } else {
        const params = category ? { category: decodeURIComponent(category) } : {};
        const response = await axios.get('/api/mcqs', { params });
        questions = response.data.mcqs || [];
      }

      // Shuffle questions for variety (but keep all assigned questions)
      if (testId) {
        // For named tests, show all assigned MCQs in their defined order
        // Don't shuffle to maintain the order set by the admin
      } else {
        // For category-based tests, shuffle for variety and limit to 10
        questions = questions.sort(() => Math.random() - 0.5).slice(0, 10);
      }

      if (questions.length === 0) {
        setError('No questions available for this category');
        return;
      }

      setMcqs(questions);

      // Initialize answers object
      const initialAnswers = {};
      questions.forEach(mcq => {
        initialAnswers[mcq.id] = null;
      });
      setAnswers(initialAnswers);

    } catch (error) {
      setError('Failed to load questions');
      console.error('MCQ fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const startTest = () => {
    setTestStarted(true);
  };

  const handleAnswerChange = (questionId, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: parseInt(answerIndex)
    }));
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < mcqs.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitTest = async (autoSubmit = false) => {
    if (hasSubmittedRef.current || submitting) {
      return;
    }
    hasSubmittedRef.current = true;
    setHasSubmitted(true);
    if (!autoSubmit) {
      setShowSubmitDialog(false);
    }

    setSubmitting(true);

    try {
      // Prepare submission data
      const mcqIds = mcqs.map(mcq => mcq.id);
      const answersArray = mcqIds.map(id => answers[id] !== null ? answers[id] : -1);

      const response = await axios.post('/api/tests/submit', {
        mcqIds,
        answers: answersArray,
        category: category ? decodeURIComponent(category) : 'Mixed',
        testId: testId || undefined,
      });

      // Navigate to results page with result data
      navigate('/results', {
        state: {
          newResult: response.data.result,
          showDetails: true
        }
      });

    } catch (error) {
      setError('Failed to submit test. Please try again.');
      console.error('Test submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.values(answers).filter(answer => answer !== null).length;
  };

  const getQuestionStatus = (index) => {
    const mcq = mcqs[index];
    if (!mcq) return 'unanswered';

    if (answers[mcq.id] !== null) return 'answered';
    if (index === currentQuestionIndex) return 'current';
    return 'unanswered';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered': return 'success';
      case 'current': return 'primary';
      default: return 'inherit';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
          <Button onClick={() => navigate('/dashboard')} sx={{ ml: 2 }}>
            Back to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!testStarted) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>
              {testId ? testName : (category ? decodeURIComponent(category) : 'Mixed')} Test
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {mcqs.length} Questions • {durationMinutes} Minutes
            </Typography>

            <Box sx={{ my: 4 }}>
              {testInstructions && (
                <>
                  <Typography variant="body1" paragraph>
                    Instructions:
                  </Typography>
                  {testInstructions.split('\n').map((line, idx) => (
                    <Typography key={idx} variant="body2" paragraph align="left">
                      {line}
                    </Typography>
                  ))}
                </>
              )}
            </Box>
            {testId ? (
              joinStatus === 'approved' ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={startTest}
                  sx={{ px: 4, py: 1.5 }}
                  disabled={loading || mcqs.length === 0}
                >
                  {loading || mcqs.length === 0 ? 'Loading questions…' : 'Start Test'}
                </Button>
              ) : joinStatus === 'pending' ? (
                <Alert severity="info">Your join request is pending approval. The Start button will appear here once approved.</Alert>
              ) : (
                <Alert severity="warning">You are not approved to take this test yet. Please request from the tests list.</Alert>
              )
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={startTest}
                sx={{ px: 4, py: 1.5 }}
              >
                Start Test
              </Button>
            )}
          </Paper>
        </Box>
      </Container>
    );
  }

  const currentMcq = mcqs[currentQuestionIndex];
  if (!currentMcq) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 2 }}>
        {/* Header with timer and progress */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center">
                <Timer sx={{ mr: 1, color: timeLeft < 300 ? 'error.main' : 'text.secondary' }} />
                <Typography
                  variant="h6"
                  color={timeLeft < 300 ? 'error.main' : 'text.primary'}
                >
                  {formatTime(timeLeft)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Typography variant="h6">
                  Question {currentQuestionIndex + 1} of {mcqs.length}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={((currentQuestionIndex + 1) / mcqs.length) * 100}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  Answered: {getAnsweredCount()}/{mcqs.length}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setShowSubmitDialog(true)}
                  startIcon={<Send />}
                  disabled={submitting || hasSubmitted}
                  sx={{ mt: 1 }}
                >
                  Submit Test
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          {/* Question Navigation */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Question Navigation
              </Typography>
              <Grid container spacing={1}>
                {mcqs.map((_, index) => {
                  const status = getQuestionStatus(index);
                  return (
                    <Grid item xs={3} key={index}>
                      <Button
                        variant={status === 'current' ? 'contained' : 'outlined'}
                        color={getStatusColor(status)}
                        size="small"
                        onClick={() => goToQuestion(index)}
                        sx={{ minWidth: 40, aspectRatio: '1' }}
                      >
                        {index + 1}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip size="small" color="success" label="" sx={{ mr: 1, minWidth: 16 }} />
                  <Typography variant="caption">Answered</Typography>
                </Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip size="small" color="primary" label="" sx={{ mr: 1, minWidth: 16 }} />
                  <Typography variant="caption">Current</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Chip size="small" color="default" label="" sx={{ mr: 1, minWidth: 16 }} />
                  <Typography variant="caption">Not Answered</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Question Content */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Question {currentQuestionIndex + 1}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {currentMcq.question}
                </Typography>
                {currentMcq.questionImageUrl && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img
                      src={currentMcq.questionImageUrl}
                      alt="Question"
                      style={{
                        maxWidth: '100%',
                        width: currentMcq.questionImageWidth ? `${currentMcq.questionImageWidth}px` : undefined,
                        height: currentMcq.questionImageHeight ? `${currentMcq.questionImageHeight}px` : 'auto'
                      }}
                    />
                  </Box>
                )}

                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={answers[currentMcq.id] !== null ? answers[currentMcq.id].toString() : ''}
                    onChange={(e) => handleAnswerChange(currentMcq.id, e.target.value)}
                  >
                    {currentMcq.options.map((option, index) => (
                      <FormControlLabel
                        key={index}
                        value={index.toString()}
                        control={<Radio />}
                        label={(() => {
                          if (typeof option === 'string') {
                            return <Typography variant="body1" sx={{ py: 0.5 }}>{option}</Typography>;
                          }
                          const text = option?.text || '';
                          const imageUrl = option?.imageUrl || '';
                          const width = option?.width ? `${option.width}px` : undefined;
                          const height = option?.height ? `${option.height}px` : undefined;
                          return (
                            <Box sx={{ py: 0.5 }}>
                              {text && (
                                <Typography variant="body1" sx={{ mb: imageUrl ? 1 : 0 }}>{text}</Typography>
                              )}
                              {imageUrl && (
                                <img src={imageUrl} alt={`Option ${index + 1}`} style={{ maxWidth: '100%', width, height }} />
                              )}
                            </Box>
                          );
                        })()}
                        sx={{
                          mb: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mx: 0,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Navigation Buttons */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<NavigateBefore />}
                  onClick={goToPreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                >
                  Previous
                </Button>

                <Typography variant="body2" color="text.secondary">
                  {currentQuestionIndex + 1} of {mcqs.length}
                </Typography>

                <Button
                  variant="outlined"
                  endIcon={<NavigateNext />}
                  onClick={goToNextQuestion}
                  disabled={currentQuestionIndex === mcqs.length - 1}
                >
                  Next
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Warning sx={{ mr: 1, color: 'warning.main' }} />
            Submit Test
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to submit your test?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have answered {getAnsweredCount()} out of {mcqs.length} questions.
            {getAnsweredCount() < mcqs.length && ' Unanswered questions will be marked as incorrect.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Time remaining: {formatTime(timeLeft)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)}>
            Continue Test
          </Button>
          <Button
            onClick={() => handleSubmitTest()}
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Submit Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TestInterface; 